import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePosts } from "../../../hooks/useCommunity";
import communityAPI from "../../../api/community";
import { calculateReadingTime } from "../../../utils/readingTime";
import Card from "../../../componentes/ui/Card";
import Badge from "../../../componentes/ui/Badge";
import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import SearchBar from "../../../componentes/ui/SearchBar";
import { PostActions } from "../shared/CRUDActions";
import { PostEditModal } from "../shared/EditModal";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import CreatePost from "../shared/CreatePost";
import { FullscreenSpinner } from '../../../componentes/LoadingIndicator';
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import { Heart, MessageCircle, Eye, Loader2 } from "lucide-react";
import FeaturedArticleFallbackImage from "../../../assets/featuredfallback.webp";
import { truncateText } from "../../../utils/truncateText";

export default function BlogList({ isOwn = false }) {
  const { user } = useCommunityAuth();

  const {
    data: popularTags = [],
    isLoading: tagsLoading,
  } = useQuery({
    queryKey: ["community", "tags", "popular"],
    queryFn: async () => {
      const res = await communityAPI.tags.getPopular();
      const payload = res.data;
      return Array.isArray(payload) ? payload : payload?.results || [];
    },
    staleTime: 60_000,
  });

  const {
    data: categoryStats = [],
    isLoading: categoriesLoading,
  } = useQuery({
    queryKey: ["community", "categories", "blog", "withCounts"],
    queryFn: async () => {
      const res = await communityAPI.categories.getWithCounts({
        post_type: "blog",
      });
      const payload = res.data;
      return Array.isArray(payload) ? payload : payload?.results || [];
    },
    staleTime: 60_000,
  });

  const {
    data: featuredPosts = [],
    isLoading: featuredLoading,
  } = useQuery({
    queryKey: ["community", "posts", "featured", { post_type: "blog" }],
    queryFn: async () => {
      const res = await communityAPI.posts.getFeatured({ post_type: "blog" });
      const payload = res.data;
      return Array.isArray(payload) ? payload : (payload?.results || []);
    },
    staleTime: 300_000,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [editingPost, setEditingPost] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearch("");
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const filters = useMemo(() => {
    const params = {
      post_type: "blog",
      ordering: "-created_at",
    };
    if (isOwn) {
      params.mine = true;
    } else {
      // Public blog listing should not show unapproved posts (even to the author).
      // Backend supports filtering via DjangoFilterBackend.
      params.is_approved = true;
    }
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (selectedCategory !== "all") {
      params.category_slug = selectedCategory;
    }
    return params;
  }, [isOwn, debouncedSearch, selectedCategory]);

  const {
    posts,
    loading: postsLoading,
    loadingMore,
    totalCount,
    hasNext,
    loadMore,
    setPage,
  } = usePosts(filters, { paginated: true, pageSize: 9, infinite: true });

  useEffect(() => {
    if (!hasInitialized && !postsLoading && !tagsLoading && !categoriesLoading && !featuredLoading) {
      setHasInitialized(true);
    }
  }, [hasInitialized, postsLoading, tagsLoading, categoriesLoading, featuredLoading]);

  const sentinelRef = useRef(null);

  const handleLoadMore = useCallback(() => {
    if (!hasNext || postsLoading || loadingMore) {
      return;
    }
    loadMore().catch(() => { });
  }, [hasNext, postsLoading, loadingMore, loadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    if (!hasNext) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore();
          }
        });
      },
      { root: null, rootMargin: "320px 0px 320px 0px", threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, hasNext]);

  const fallbackCategories = useMemo(() => {
    const map = new Map();
    posts.forEach((post) => {
      const category = post.category;
      if (!category) return;
      const value =
        category.slug ||
        (category.id !== undefined ? String(category.id) : category.name);
      if (!value || map.has(value)) {
        return;
      }
      map.set(value, {
        label: category.name || "General",
        value,
        count: null,
      });
    });
    return Array.from(map.values());
  }, [posts]);

  const categoryOptions = useMemo(() => {
    const options = [
      { label: "All Categories", value: "all", count: totalCount },
    ];
    const seen = new Set(options.map((option) => option.value));
    (categoryStats || []).forEach((cat) => {
      const value =
        cat.slug || (cat.id !== undefined ? String(cat.id) : cat.name);
      if (!value || seen.has(value)) {
        return;
      }
      options.push({
        label: cat.name,
        value,
        count: cat.post_count ?? 0,
      });
      seen.add(value);
    });
    fallbackCategories.forEach((option) => {
      if (!seen.has(option.value)) {
        options.push(option);
        seen.add(option.value);
      }
    });
    return options;
  }, [categoryStats, fallbackCategories, totalCount]);

  const categoryOptionMap = useMemo(() => {
    const map = new Map();
    categoryOptions.forEach((option) => {
      map.set(option.value, option);
    });
    return map;
  }, [categoryOptions]);

  useEffect(() => {
    if (
      selectedCategory !== "all" &&
      !categoryOptionMap.has(selectedCategory)
    ) {
      setSelectedCategory("all");
    }
  }, [selectedCategory, categoryOptionMap]);

  const selectedCategoryLabel =
    categoryOptionMap.get(selectedCategory)?.label || "All Categories";

  const getCategoryCount = (value) => {
    if (value === "all") {
      return totalCount;
    }
    return categoryOptionMap.get(value)?.count;
  };

  const featuredPost = featuredPosts.length > 0 ? featuredPosts[0] : null;

  const filteredPosts = useMemo(
    () => posts.filter((p) => p.id !== featuredPost?.id),
    [posts, featuredPost]
  );

  const showInitialLoader =
    !hasInitialized && (postsLoading || tagsLoading || categoriesLoading || featuredLoading);
  const showPostsLoading = hasInitialized && postsLoading;

  const getAvatarSrc = (account) => {
    if (!account) return undefined;
    return account.avatar_url || account.avatar || undefined;
  };

  const strip = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const getExcerpt = (html, maxLength = 160) => {
    const text = strip(html).replace(/\s+/g, " ").trim();
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength).trimEnd()}…`;
  };

  const getFirstImage = (html) => {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const img = doc.querySelector("img");
    return img ? img.src : null;
  };

  const getPostLink = (post) => {
    if (!post) return isOwn ? "/community/blog" : "/blog";
    const base = isOwn ? "/community/blog" : "/blog";
    return post.slug ? `${base}/${post.slug}` : `${base}/${post.id}`;
  };

  const buildDetailLink = (post) => ({
    pathname: getPostLink(post),
    state: { fromPublic: !isOwn },
  });

  if (showInitialLoader)
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <FullscreenSpinner message="Loading community posts..." />
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap');
        .blog-title { font-family: 'Crimson Pro', Georgia, serif; }
        .blog-body { font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <h1 className="blog-title text-4xl sm:text-5xl font-bold text-gray-950 dark:text-white leading-tight tracking-tight mb-4">
            Latest Insights
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Discover cutting-edge cybersecurity insights and
            industry best practices
          </p>
        </div>

        {featuredPost && (
          <div className="mb-12">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group flex flex-col min-h-[380px] sm:min-h-[450px] md:min-h-[400px]">
              <img
                src={
                  featuredPost.cover_image_url ||
                  featuredPost.image ||
                  getFirstImage(featuredPost.content) ||
                  FeaturedArticleFallbackImage
                }
                alt={featuredPost.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent pointer-events-none" />
              <div className="relative mt-auto p-5 pb-6 md:p-10 z-10 w-full">
                <div className="max-w-3xl">
                  <span className="inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-medium mb-4">
                    Featured Article
                  </span>
                  <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4 line-clamp-3">
                    {featuredPost.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 text-white/90 mb-6">
                    {featuredPost.author?.username ? (
                      <Link
                        to={`/profile/${featuredPost.author.username}`}
                        className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                      >
                        <Avatar
                          src={getAvatarSrc(featuredPost.author)}
                          name={[featuredPost.author?.first_name, featuredPost.author?.last_name].filter(Boolean).join(" ").trim() || featuredPost.author?.name || featuredPost.author?.username || "Anonymous"}
                          size={10}
                        />
                        <div className="text-sm">
                          <div className="font-medium flex items-center gap-1.5">
                            {truncateText([featuredPost.author?.first_name, featuredPost.author?.last_name].filter(Boolean).join(" ").trim() || featuredPost.author?.name || featuredPost.author?.username, 25)}
                            {(featuredPost.author.is_trusted || featuredPost.author.status?.is_trusted) && (
                              <img
                                src={verifiedBadge}
                                alt="Verified"
                                className="w-4 h-4 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                              />
                            )}
                          </div>
                          <div className="opacity-80 flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>{formatDate(featuredPost.created_at)}</span>
                            <span className="flex items-center gap-1.5">
                              <Eye className="w-4 h-4" />{" "}
                              {formatNumber(featuredPost.views || 0)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Heart className="w-4 h-4" />{" "}
                              {formatNumber(
                                featuredPost.likes_total ??
                                  featuredPost.likes_count ??
                                  0
                              )}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <MessageCircle className="w-4 h-4" />{" "}
                              {formatNumber(
                                featuredPost.comments_count ??
                                  featuredPost.comments_total ??
                                  0
                              )}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <>
                        <Avatar
                          src={getAvatarSrc(featuredPost.author)}
                          name="Anonymous"
                          size={10}
                        />
                        <div className="text-sm">
                          <div className="font-medium">Anonymous</div>
                          <div className="opacity-80 flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>{formatDate(featuredPost.created_at)}</span>
                            <span className="flex items-center gap-1.5">
                              <Eye className="w-4 h-4" />{" "}
                              {formatNumber(featuredPost.views || 0)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Heart className="w-4 h-4" />{" "}
                              {formatNumber(
                                featuredPost.likes_total ??
                                  featuredPost.likes_count ??
                                  0
                              )}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <MessageCircle className="w-4 h-4" />{" "}
                              {formatNumber(
                                featuredPost.comments_count ??
                                  featuredPost.comments_total ??
                                  0
                              )}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <Link
                    to={buildDetailLink(featuredPost)}
                    className="inline-block px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                  >
                    Read Article
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6 mb-12">
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              onClear={handleClearSearch}
            />
          </div>

          <div className="relative w-full max-w-sm">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {selectedCategoryLabel}
              </span>
              <svg
                className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40 bg-black/5"
                  onClick={() => setIsDropdownOpen(false)}
                />

                <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                  <div className="max-h-64 overflow-y-auto py-1">
                    {categoryOptions.map((option) => {
                      const count = getCategoryCount(option.value);
                      if (
                        option.value !== "all" &&
                        typeof count === "number" &&
                        count === 0
                      ) {
                        return null;
                      }

                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSelectedCategory(option.value);
                            setIsDropdownOpen(false);
                            setPage(1);
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 transition-all duration-150 group ${selectedCategory === option.value
                              ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary/80 border-l-4 border-l-indigo-600"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            }`}
                        >
                          <span className="font-medium">{option.label}</span>
                          {typeof count === "number" ? (
                            <span className="ml-4 px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors duration-150">
                              {formatNumber(count)}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {user && (
            <div className="flex items-center">
              <button
                onClick={() => setShowCreate(true)}
                className="px-6 py-3 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                New Post
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {showPostsLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={`post-skeleton-${index}`} className="group relative">
                    <div className="overflow-hidden border-0 shadow-lg bg-white dark:bg-[#161616] rounded-3xl animate-pulse">
                      <div className="h-48 bg-gray-200 dark:bg-gray-700" />
                      <div className="p-6 space-y-4">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm text-gray-400">
                            <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                            <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : filteredPosts.length === 0 ? (
                <div className="col-span-full text-center py-16 text-gray-600 dark:text-gray-400">
                  No posts found
                </div>
              ) : (
                filteredPosts.map((p) => (
                  <article key={p.id} className="group relative">
                    <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white dark:bg-[#161616]">
                      {isOwn && (
                        <div className="absolute top-2 right-2 z-10">
                          <PostActions
                            post={p}
                            onEdit={() => setEditingPost(p)}
                            onDeleted={() => {
                              /* no-op; list updates via hook */
                            }}
                          />
                        </div>
                      )}
                      <div className="relative overflow-hidden">
                        {(() => {
                          const displayImg = p.cover_image_url || p.image || getFirstImage(p.content);
                          if (displayImg) {
                            return (
                              <img
                                src={displayImg}
                                alt={p.title}
                                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            );
                          }
                          return (
                            <div className="w-full h-48 bg-gradient-to-br from-primary via-secondary to-primary flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                              <svg
                                className="w-20 h-20 text-white/30"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                                />
                              </svg>
                            </div>
                          );
                        })()}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute top-4 left-4">
                          {p.category && (
                            <Badge color="blue" className="shadow-lg">
                              {p.category.name}
                            </Badge>
                          )}
                        </div>
                        {!p.is_approved && (
                          <div className="absolute bottom-4 right-4">
                            <Badge
                              color="yellow"
                              className="text-xs whitespace-nowrap"
                            >
                              Pending
                            </Badge>
                          </div>
                        )}
                      </div>
                      <div className="p-6 flex flex-col h-full">
                        <Link to={buildDetailLink(p)}>
                          <h3 className="font-bold text-xl leading-tight mb-3 text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors duration-200 min-h-[3.2rem] overflow-hidden break-words whitespace-normal [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                            {p.title}
                          </h3>
                        </Link>
                        <p className="text-gray-600 dark:text-gray-300 mb-4 min-h-[3rem] overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {getExcerpt(p.content, 160)}
                        </p>
                        <div className="space-y-4 mt-auto">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {p.author?.username ? (
                                <Link
                                  to={`/profile/${p.author.username}`}
                                  className="flex items-center gap-3"
                                >
                                  <Avatar
                                    src={getAvatarSrc(p.author)}
                                    name={[p.author?.first_name, p.author?.last_name].filter(Boolean).join(" ").trim() || p.author?.name || p.author?.username || "Anonymous"}
                                    size={8}
                                  />
                                  <div className="text-sm">
                                    <div className="font-medium text-gray-900 dark:text-white truncate max-w-xs flex items-center gap-1.5">
                                      {truncateText([p.author?.first_name, p.author?.last_name].filter(Boolean).join(" ").trim() || p.author?.name || p.author?.username, 25)}
                                      {(p.author.is_trusted || p.author.status?.is_trusted) && (
                                        <img
                                          src={verifiedBadge}
                                          alt="Verified"
                                          className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                                        />
                                      )}
                                    </div>
                                    <div className="text-gray-500 dark:text-gray-400">
                                      {formatDate(p.created_at)} ·{" "}
                                      {calculateReadingTime(p.content)} min read
                                    </div>
                                  </div>
                                </Link>
                              ) : (
                                <>
                                  <Avatar
                                    src={getAvatarSrc(p.author)}
                                    name="Anonymous"
                                    size={8}
                                  />
                                  <div className="text-sm">
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      Anonymous
                                    </div>
                                    <div className="text-gray-500 dark:text-gray-400">
                                      {formatDate(p.created_at)}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Heart className="w-4 h-4" strokeWidth={2} />
                                {formatNumber(p.likes_count || 0)}
                              </div>
                              <div className="flex items-center gap-1">
                                <MessageCircle
                                  className="w-4 h-4"
                                  strokeWidth={2}
                                />
                                {formatNumber(p.comments_count || 0)}
                              </div>
                              <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" strokeWidth={2} />
                                {formatNumber(p.views || 0)}
                              </div>
                            </div>
                            <Link
                              to={buildDetailLink(p)}
                              className="flex items-center gap-1 text-primary dark:text-primary/80 font-semibold hover:text-[#229bb8] dark:hover:text-primary/70 transition-colors duration-200"
                            >
                              Read
                              <svg
                                className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </article>
                ))
              )}
            </div>
            <div ref={sentinelRef} className="h-2 w-full" />
            <div className="mt-10 flex flex-col items-center gap-4">
              {loadingMore && (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading more posts...</span>
                </div>
              )}
              {!loadingMore && hasNext && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="px-5 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#161616] text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  Load more posts
                </button>
              )}
              {!hasNext && posts.length > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You&apos;ve reached the end of the feed.
                </p>
              )}
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="space-y-8 sticky top-24">
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Featured Posts
                  </h4>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {featuredPosts.length ? (
                    featuredPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={buildDetailLink(post)}
                        className="block px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <h5 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                          {truncateText(post.title, 60)}
                        </h5>
                        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                          <span>{post.category?.name || "General"}</span>
                          <span>·</span>
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" /> {formatNumber(post.views || 0)}</span>
                            <span className="flex items-center gap-1" title="Likes"><Heart className="w-3.5 h-3.5" /> {formatNumber(post.likes_count || 0)}</span>
                            <span className="flex items-center gap-1" title="Comments"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(post.comments_count || 0)}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
                      No featured posts available right now.
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white dark:bg-[#161616] p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                <h4 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
                  Popular Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {popularTags.length ? (
                    popularTags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => setSearchTerm(tag.name)}
                        className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-primary/20 dark:hover:bg-primary/20 hover:text-[#229bb8] dark:hover:text-primary/70 transition-colors duration-200"
                        title={`${formatNumber(tag.post_count)} posts`}
                        type="button"
                      >
                        #{tag.name}
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No popular tags available.
                    </p>
                  )}
                </div>
              </div>


            </div>
          </aside>
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-[#161616] rounded-2xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Create New Post
              </h3>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <CreatePost type="blog" />
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      <PostEditModal
        visible={!!editingPost}
        post={editingPost}
        onClose={() => setEditingPost(null)}
        onUpdated={() => {
          window.location.reload();
        }}
      />
    </div>
  );
}

BlogList.propTypes = {
  isOwn: PropTypes.bool,
};
